/**
 * StudioSidebar — Persistent 17-item left sidebar for MarkeeMix Studio.
 *
 * Sections:
 *  · Workspace (Dashboard, Channels, Notifications, Search)
 *  · My Work (My Tasks, Approvals)
 *  · AI Tools (Topic Suggestions)
 *  · Production (Calendar, Workload, Scorecard, Sponsorships)
 *  · Role Dashboards (Editor, Designer, SMM, Creator)
 *  · Insights (Reports)
 *  · System (Trash, Settings)
 *
 * Collapsible. Mobile-aware (hidden under 900px, hamburger toggles).
 */
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useStudioShortcuts, ShortcutsHelp } from "./useStudioShortcuts";
import { ImpersonationBanner } from "./StudioSuperAdmin";
import {
  LayoutDashboard, Youtube, Bell, Search, ListChecks, CheckCircle2,
  Wand2, Calendar, Users, Trophy, Megaphone, Video, Image as ImageIcon,
  Hash, ScrollText, FileText, Trash, Settings, ChevronsLeft, ChevronsRight,
  Menu, X, Receipt, Activity, Sparkles, Crown, AlertTriangle,
} from "lucide-react";

const NAV_SECTIONS = [
  {
    label: "Workspace",
    items: [
      { to: "/studio", end: true, icon: LayoutDashboard, label: "Command center", testid: "sb-dashboard" },
      { to: "/studio/operations", icon: Activity, label: "Operations health", testid: "sb-operations" },
      { to: "/studio/triage", icon: AlertTriangle, label: "Triage hub", testid: "sb-triage" },
      { to: "/studio/channels", icon: Youtube, label: "Channels", testid: "sb-channels" },
      { to: "/studio/notifications", icon: Bell, label: "Notifications", testid: "sb-notifications" },
      { to: "/studio/search", icon: Search, label: "Global search", testid: "sb-search" },
    ],
  },
  {
    label: "My Work",
    items: [
      { to: "/studio/my-tasks", icon: ListChecks, label: "My tasks", testid: "sb-my-tasks" },
      { to: "/studio/approvals", icon: CheckCircle2, label: "Approvals", testid: "sb-approvals" },
    ],
  },
  {
    label: "AI Tools",
    items: [
      { to: "/studio/topic-suggestions", icon: Wand2, label: "AI Topic Suggestions", testid: "sb-topics" },
      { to: "/studio/seo", icon: Sparkles, label: "AI SEO Assistant", testid: "sb-seo" },
      { to: "/studio/memory", icon: Activity, label: "AI Channel Memory", testid: "sb-memory" },
      { to: "/studio/pillars", icon: FileText, label: "Content Pillars (CSV)", testid: "sb-pillars" },
      { to: "/studio/assets", icon: FileText, label: "PPT / PDF Assets", testid: "sb-assets" },
    ],
  },
  {
    label: "Production",
    items: [
      { to: "/studio/calendar", icon: Calendar, label: "Calendar", testid: "sb-calendar" },
      { to: "/studio/workload", icon: Users, label: "Workload balancer", testid: "sb-workload" },
      { to: "/studio/scorecard", icon: Trophy, label: "Team scorecard", testid: "sb-scorecard" },
      { to: "/studio?tab=sponsorships", icon: Megaphone, label: "Sponsorships", testid: "sb-sponsorships" },
    ],
  },
  {
    label: "Role Dashboards",
    items: [
      { to: "/studio/role/editor", icon: Video, label: "Editor", testid: "sb-role-editor" },
      { to: "/studio/role/designer", icon: ImageIcon, label: "Designer", testid: "sb-role-designer" },
      { to: "/studio/role/smm", icon: Hash, label: "SMM", testid: "sb-role-smm" },
      { to: "/studio/role/creator", icon: ScrollText, label: "Creator", testid: "sb-role-creator" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/studio/analytics", icon: Activity, label: "Analytics", testid: "sb-analytics" },
      { to: "/studio/reports", icon: FileText, label: "Reports & PDF", testid: "sb-reports" },
      { to: "/studio/billing", icon: Receipt, label: "Billing", testid: "sb-billing" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/studio/super", icon: Crown, label: "Super Admin", testid: "sb-super" },
      { to: "/studio/trash", icon: Trash, label: "Trash", testid: "sb-trash" },
      { to: "/studio/settings", icon: Settings, label: "Settings", testid: "sb-settings" },
    ],
  },
];

export default function StudioSidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  return (
    <>
      {mobileOpen && (
        <div className="studio-sb-backdrop" onClick={onMobileClose} data-testid="sb-backdrop" />
      )}
      <aside
        className={`studio-sb ${collapsed ? "studio-sb--collapsed" : ""} ${mobileOpen ? "studio-sb--mobile-open" : ""}`}
        data-testid="studio-sidebar"
        aria-label="Studio navigation"
      >
        <div className="studio-sb-head">
          <span className="studio-sb-brand">
            <Youtube size={18} />
            {!collapsed && <strong>Studio CRM</strong>}
          </span>
          <button
            className="studio-sb-collapse"
            onClick={onToggle}
            data-testid="sb-collapse-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
          </button>
          <button
            className="studio-sb-mobile-close"
            onClick={onMobileClose}
            data-testid="sb-mobile-close"
            aria-label="Close sidebar"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="studio-sb-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label} className="studio-sb-section">
              {!collapsed && <p className="studio-sb-section-label">{section.label}</p>}
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to + item.label}
                    to={item.to}
                    end={item.end}
                    onClick={onMobileClose}
                    className={({ isActive }) =>
                      `studio-sb-item ${isActive ? "studio-sb-item--active" : ""}`
                    }
                    data-testid={item.testid}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={16} strokeWidth={1.8} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {!collapsed && (
          <div className="studio-sb-foot">
            <small>MarkeeMix Studio · v2</small>
          </div>
        )}
      </aside>
    </>
  );
}

export function StudioLayout({ children }) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("studio-sb-collapsed") === "1";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const { helpOpen, setHelpOpen } = useStudioShortcuts();

  useEffect(() => {
    try { localStorage.setItem("studio-sb-collapsed", collapsed ? "1" : "0"); } catch (e) { /* noop */ }
  }, [collapsed]);

  return (
    <div className={`studio-layout ${collapsed ? "studio-layout--collapsed" : ""}`}>
      <ImpersonationBanner />
      <StudioSidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <button
        className="studio-sb-mobile-trigger"
        onClick={() => setMobileOpen(true)}
        data-testid="sb-mobile-trigger"
        aria-label="Open sidebar"
      >
        <Menu size={18} />
      </button>
      <div className="studio-layout-main">{children}</div>
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
